import axios from "axios";
import appConfig from "../../config";
import { isTokenInvalidatedError } from "../../hooks/queries/useAthenaAPIClient/utils";
import type { MicroServices } from "../../interfaces/Config";
import {
  applyConfigOverrides,
  getConfigOverrides,
  logConfigOverrides,
} from "../configOverrides";
import { msConfigResolver } from "../init";
import * as localStorage from "../localStorage";

export default class AxiosClient {
  private _apiUrls?: MicroServices;
  private _apiToken?: string;
  private _tokenInvalidatedInterceptor:
    | undefined
    | {
        id: number;
        callback: () => void;
      };
  private _deadSession: boolean = false;
  private _onSessionDead?: () => void;
  public axios;

  get apiUrls(): MicroServices {
    if (!this._apiUrls) {
      console.log(
        "apiUrls is undefined! AthenaAPIClient is still initializing",
      );
      throw Error;
    }
    return this._apiUrls;
  }

  get apiToken(): string {
    if (!this._apiToken) {
      console.log("apiToken is undefined! App is still initializing");
      throw Error;
    }
    return this._apiToken;
  }

  set apiToken(apiToken: string) {
    this._apiToken = apiToken;
  }

  get tokenInvalidatedInterceptor() {
    return this._tokenInvalidatedInterceptor?.callback;
  }

  /**
   * Registers a callback fired once when the session becomes irrecoverable
   * Will display SessionGuard
   */
  onSessionDead(callback: () => void) {
    this._onSessionDead = callback;
  }

  private sessionIsDead() {
    const wasAlreadyDead = this._deadSession;
    this._deadSession = true;
    if (!wasAlreadyDead) this._onSessionDead?.();
  }

  constructor() {
    this.axios = axios.create();
  }

  private async renewApiToken(error: any) {
    const status = error.response?.status;
    // Not an auth error (500, network failure…): none of our business, don't
    // touch session state.
    if (status !== 401 && status !== 403) throw error;

    if (this._deadSession) {
      console.log(
        "Session is known to be dead. No refresh before the session is revived",
      );
      throw error;
    }

    const originalRequest = error.config;
    // We were already retrying or refreshing the token. Killing session to avoid infinite loop and laucnhing the Session Guard
    if (
      originalRequest._retry ||
      originalRequest.url.includes(REFRESH_TOKEN_ROUTE)
    ) {
      console.error("Renewing the token has failed, marking session dead...");
      this.sessionIsDead();
      throw error;
    }

    // Retry once before defering to the Session Guard
    originalRequest._retry = true;
    try {
      const newApiToken = await this.getAndUpdateToken();
      originalRequest.headers["Authorization"] = `Bearer ${newApiToken}`;
      return await this.axios.request(originalRequest);
    } catch {
      console.error(
        "Renewing the token has failed, marking the session dead..",
      );
      this.sessionIsDead();
      throw error;
    }
  }

  async getAndUpdateToken(): Promise<string> {
    try {
      const headers = { Authorization: `Bearer ${this.apiToken}` };
      // URL set in .env for now
      const res = await this.axios.get(
        `${this.apiUrls.endpoints.auth}${REFRESH_TOKEN_ROUTE}`,
        {
          headers: headers,
        },
      );
      const newToken = res.data.token;
      this._apiToken = newToken;
      localStorage.setItem(localStorage.Key.TOKEN, newToken);
      this._deadSession = false;
      return res.data.token as Promise<string>;
    } catch (error) {
      console.log(error);
      throw error;
    }
  }

  async init(): Promise<MicroServices | any> {
    const origin = window.location.origin;
    try {
      const { data } = await this.axios.get(
        `${origin + (appConfig.basePath ?? "/")}json/msConfigs.json`,
      );
      try {
        const resolvedConfig = msConfigResolver(data, window.location.href);
        const overrides = getConfigOverrides();
        if (overrides) {
          logConfigOverrides(overrides);
          this._apiUrls = applyConfigOverrides(resolvedConfig, overrides);
        } else {
          this._apiUrls = resolvedConfig;
        }
      } catch (error) {
        return error;
      }
    } catch (error) {
      return error;
    }

    this.axios.interceptors.response.use(
      undefined,
      this.renewApiToken.bind(this),
    );
    return this._apiUrls;
  }

  updateTokenInvalidatedInterceptor(callback: () => void) {
    if (typeof this._tokenInvalidatedInterceptor !== "undefined") {
      this.axios.interceptors.response.eject(
        this._tokenInvalidatedInterceptor.id,
      );
      this._tokenInvalidatedInterceptor = undefined;
    }
    const id = this.axios.interceptors.response.use(undefined, (error) => {
      if (isTokenInvalidatedError(error)) {
        callback();
      }
      throw error;
    });
    this._tokenInvalidatedInterceptor = { id, callback };
  }
}

export const axiosClient = new AxiosClient();

const REFRESH_TOKEN_ROUTE = "/session/refresh-token";
